import { FieldArray, Form, Formik } from "formik"
import { useMemo } from "react"
import { Accordion, Col, FormCheck } from "react-bootstrap"

import TextInput from "@/components/Inputs/TextInput"
import { MODALS } from "@/constants/modals"
import type {
  CourseContent,
  CourseContentItem,
  Lesson,
  Topic,
} from "@/types/courseTypes"
import { useStore } from "@/zustand/store"
import { useTranslation } from "react-i18next"

export default function MindMapModal() {
  const modalData = useStore(
    (store) => store.modalsStates[MODALS.round.mindMap]
  )

  const { course_content } = modalData || {}

  const initialValues = useMemo(() => {
    if (!course_content) {
      return [] as CourseContent
    }

    const round_content = course_content.map((chapter) => ({
      id: chapter.id,
      order: chapter.order,
      name: chapter.name || "",
      is_checked: chapter.is_checked ?? "0",
      lessons: Array.isArray(chapter.lessons)
        ? chapter.lessons.map((lesson) => ({
            id: lesson.id,
            title: lesson.title || "",
            order: lesson.order,
            is_checked: lesson.is_checked ?? "0",
            topics: Array.isArray(lesson.topics)
              ? lesson.topics.map((topic) => ({
                  id: topic.id,
                  order: topic.order,
                  title: topic.title || "",
                  is_checked: topic.is_checked ?? "0",
                }))
              : [],
          }))
        : [],
    }))
    return round_content as CourseContent
  }, [course_content])

  const { t } = useTranslation()

  return (
    <Formik
      initialValues={{ round_content: initialValues }}
      onSubmit={() => {}}
    >
      {({ values }) => {
        return (
          <Form>
            <Col md={12} className="mb-3">
              <p className="fw-bold mb-2">
                {t("enrolledCoursesModalsTitles.mindMap")}
              </p>
              <FieldArray
                name="round_content"
                render={() => (
                  <Accordion defaultActiveKey="0">
                    {values.round_content.map((chapter, chapterIndex) => (
                      <ChapterItem
                        key={chapter.id}
                        chapterIndex={chapterIndex}
                        chapter={chapter}
                      />
                    ))}
                  </Accordion>
                )}
              />
            </Col>
          </Form>
        )
      }}
    </Formik>
  )
}

function TopicItem({
  topic,
  chapterIndex,
  lessonIndex,
  topicIndex,
}: {
  topic: Topic
  chapterIndex: number
  lessonIndex: number
  topicIndex: number
}) {
  return (
    <div key={topicIndex} className="d-flex mb-2 ps-5 gap-2">
      <TextInput
        readOnly
        id={`round_content.${chapterIndex}.lessons.${lessonIndex}.topics.${topicIndex}.title`}
        label={``}
        placeholder={`Topic ${topicIndex + 1}`}
        name={`round_content.${chapterIndex}.lessons.${lessonIndex}.topics.${topicIndex}.title`}
        as="field"
      />
      <FormCheck
        readOnly
        type="checkbox"
        className="mt-3"
        checked={topic.is_checked === "1"}
      />
    </div>
  )
}

function LessonItem({
  lesson,
  lessonIndex,
  chapterIndex,
}: {
  lesson: Lesson
  lessonIndex: number
  chapterIndex: number
}) {
  return (
    <div key={lessonIndex}>
      <div className="d-flex align-items-center mb-2 ps-4 gap-2">
        <TextInput
          readOnly
          id={`round_content.${chapterIndex}.lessons.${lessonIndex}.title`}
          label={`Lesson ${lessonIndex + 1}`}
          placeholder={`Lesson ${lessonIndex + 1}`}
          name={`round_content.${chapterIndex}.lessons.${lessonIndex}.title`}
          as="field"
        />
        <FormCheck
          readOnly
          type="checkbox"
          className="mt-3"
          checked={lesson.is_checked === "1"}
        />
      </div>

      <FieldArray
        name={`round_content.${chapterIndex}.lessons.${lessonIndex}.topics`}
        render={() => (
          <>
            {lesson.topics?.map((topic, topicIndex) => (
              <TopicItem
                key={topic.id}
                topic={topic}
                chapterIndex={chapterIndex}
                lessonIndex={lessonIndex}
                topicIndex={topicIndex}
              />
            ))}
          </>
        )}
      />
    </div>
  )
}

function ChapterItem({
  chapterIndex,
  chapter,
}: {
  chapterIndex: number
  chapter: CourseContentItem
}) {
  return (
    <Accordion.Item eventKey={String(chapterIndex)} key={chapterIndex}>
      <Accordion.Header>
        {chapter.name || `Chapter ${chapterIndex + 1}`}
      </Accordion.Header>
      <Accordion.Body>
        <div className="d-flex align-items-center gap-2  mb-2">
          <TextInput
            readOnly
            id={`round_content.${chapterIndex}.name`}
            label={`Chapter ${chapterIndex + 1}`}
            placeholder="Chapter title"
            name={`round_content.${chapterIndex}.name`}
            as="field"
          />
          <FormCheck
            readOnly
            type="checkbox"
            className="mt-3"
            checked={chapter.is_checked === "1"}
          />
        </div>

        <FieldArray
          name={`round_content.${chapterIndex}.lessons`}
          render={() => (
            <>
              {chapter.lessons.map((lesson, lessonIndex) => (
                <LessonItem
                  key={lessonIndex}
                  lesson={lesson}
                  lessonIndex={lessonIndex}
                  chapterIndex={chapterIndex}
                />
              ))}
            </>
          )}
        />
      </Accordion.Body>
    </Accordion.Item>
  )
}
